import { useEffect } from 'react';

import { Icon } from '@dcc-erp/ui';

import { HelpBlocks } from './HelpBlocks';
import { getGuide } from '../../help';

// Right slide-over that renders an in-app user guide (text + screenshots).
// Distinct from the centered AppModal — this is a dedicated help pattern.
export function HelpDrawer({ guideKey, onClose }: { guideKey: string; onClose: () => void }) {
  const guide = getGuide(guideKey);

  useEffect(() => {
    // Only lock scroll + bind Escape when an actual drawer is shown — otherwise an
    // unknown guideKey renders null but would still trap body scroll with no ✕.
    if (!guide) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, guide]);

  if (!guide) return null;

  function jump(id: string) {
    document
      .getElementById(`help-sec-${id}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 200,
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <div
        role="dialog"
        aria-label={`คู่มือ: ${guide.title}`}
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 520,
          maxWidth: '100vw',
          height: '100%',
          background: '#fff',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-8px 0 30px rgba(0,0,0,0.18)',
        }}
      >
        <div
          style={{
            padding: '14px 18px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 12,
          }}
        >
          <div>
            <h3 style={{ margin: 0 }}>{guide.title}</h3>
            {guide.subtitle && <div className="meta">{guide.subtitle}</div>}
            <div className="meta">{guide.updated}</div>
          </div>
          <button type="button" className="btn sm ghost" aria-label="ปิด" onClick={onClose}>
            <Icon name="x" size={16} />
          </button>
        </div>

        <div
          style={{
            padding: '10px 18px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
          }}
        >
          {guide.sections.map((s) => (
            <button
              key={s.id}
              type="button"
              className="btn sm ghost"
              style={{ fontSize: 12 }}
              onClick={() => jump(s.id)}
            >
              {s.heading}
            </button>
          ))}
        </div>

        <div style={{ padding: '16px 18px', overflowY: 'auto', flex: 1 }}>
          {guide.sections.map((s) => (
            <section key={s.id} id={`help-sec-${s.id}`} style={{ marginBottom: 18 }}>
              <h4 style={{ margin: '0 0 8px', color: 'var(--brand)', fontSize: 16 }}>
                {s.heading}
              </h4>
              <HelpBlocks blocks={s.blocks} />
            </section>
          ))}
        </div>

        {guide.docxUrl && (
          <div
            style={{
              padding: '10px 18px',
              borderTop: '1px solid var(--border)',
              background: '#fafafa',
            }}
          >
            <a className="btn sm" href={guide.docxUrl} download>
              <Icon name="download" size={14} /> ดาวน์โหลด Word
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
